import { createContext, type ReactNode, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router';
import { useT } from '@/i18n';
import type { Currency, IsoDate, IsoMonth, Money, Timestamp } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import type { DateStyle } from '@/lib/format/date';
import { type Dec, toDec } from '@/lib/format/money';
import { useFormat } from '@/lib/format/useFormat';
import { Button } from './Button';
import { BackChevron, ForwardChevron } from './icons';

// ------------------------------------------------------------------ Money / dates

/**
 * An amount in the school's locale and currency. <bdi> isolates the digits so
 * a negative sign or Latin digits never reorder the surrounding Arabic text.
 */
export function MoneyText({
  value,
  currency,
  className,
  signed,
  tone,
  symbol = true,
}: {
  value: Money | Dec | null | undefined;
  currency?: Currency;
  className?: string;
  signed?: boolean;
  /** "auto": green above zero, amber below. */
  tone?: 'auto';
  symbol?: boolean;
}) {
  const fmt = useFormat();
  const d = toDec(value);
  return (
    <bdi
      className={cn(
        'whitespace-nowrap tabular-nums',
        tone === 'auto' && (d.isZero() ? 'text-success' : d.isNegative() ? 'text-warning-text' : 'text-success'),
        className,
      )}
    >
      {fmt.money(value, { currency, signed, symbol })}
    </bdi>
  );
}

export function DateText({ value, format, className }: { value: IsoDate | Timestamp | null | undefined; format?: DateStyle; className?: string }) {
  const fmt = useFormat();
  if (!value) return <span className={className}>—</span>;
  return (
    <time dateTime={value.slice(0, 10)} className={cn('whitespace-nowrap', className)}>
      {fmt.date(value, format)}
    </time>
  );
}

export function MonthText({ value, className }: { value: IsoMonth | IsoDate | Timestamp | null | undefined; className?: string }) {
  const fmt = useFormat();
  if (!value) return <span className={className}>—</span>;
  return (
    <time dateTime={value.slice(0, 7)} className={cn('whitespace-nowrap', className)}>
      {fmt.month(value)}
    </time>
  );
}

export function DateTimeText({ value, className }: { value: Timestamp | null | undefined; className?: string }) {
  const fmt = useFormat();
  if (!value) return <span className={className}>—</span>;
  return (
    <time dateTime={value} className={cn('whitespace-nowrap', className)}>
      {fmt.dateTime(value)}
    </time>
  );
}

/**
 * A thin vertical rule between inline items. Used instead of "·", which next
 * to Arabic-Indic digits reads like the zero "٠".
 */
export function Sep() {
  return <span aria-hidden className="mx-2 inline-block h-[0.85em] w-px bg-current align-[-0.05em] opacity-30" />;
}

/** Codes, phones and references: always LTR, never reordered. */
export function Ltr({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <bdi dir="ltr" className={cn('whitespace-nowrap', className)}>
      {children}
    </bdi>
  );
}

// ------------------------------------------------------------------ Page header

/** The shell's sticky header exposes a slot; PageHeader renders into it. */
export const HeaderSlotContext = createContext<HTMLElement | null>(null);

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  const slot = useContext(HeaderSlotContext);
  const t = useT();
  useEffect(() => {
    document.title = `${title} · ${t('app.name')}`;
  }, [title, t]);
  const content = (
    <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex min-w-0 flex-col">
        <h1 className="truncate text-xl font-semibold">{title}</h1>
        {subtitle ? <div className="truncate text-sm text-subtle">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
  return slot ? createPortal(content, slot) : <div className="mb-5">{content}</div>;
}

// ------------------------------------------------------------------ Tabs

export interface TabItem {
  key: string;
  label: ReactNode;
  count?: number;
  to?: string;
}

const tabClass = (active: boolean) =>
  cn(
    'relative inline-flex h-11 shrink-0 items-center gap-2 px-3 text-base whitespace-nowrap transition-colors',
    active ? 'font-semibold text-ink after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-ink' : 'text-subtle hover:text-ink',
  );

/** Router tabs (NavLink) when items have `to`, otherwise local tabs. */
export function Tabs({ items, value, onChange, className, label }: { items: TabItem[]; value?: string; onChange?: (key: string) => void; className?: string; label: string }) {
  const fmt = useFormat();
  const count = (n?: number) =>
    n === undefined ? null : <span className="rounded-sm bg-track px-1.5 text-xs font-medium text-muted">{fmt.number(n)}</span>;
  return (
    <nav aria-label={label} className={cn('-mx-1 flex overflow-x-auto border-b border-line', className)}>
      {items.map((item) =>
        item.to ? (
          <NavLink key={item.key} to={item.to} end className={({ isActive }) => tabClass(isActive)}>
            {item.label}
            {count(item.count)}
          </NavLink>
        ) : (
          <button key={item.key} type="button" aria-pressed={value === item.key} onClick={() => onChange?.(item.key)} className={tabClass(value === item.key)}>
            {item.label}
            {count(item.count)}
          </button>
        ),
      )}
    </nav>
  );
}

/** Small pill filters (status, method…). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: { value: T; label: ReactNode; count?: number }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  const fmt = useFormat();
  return (
    <div role="radiogroup" aria-label={label} className={cn('flex flex-wrap gap-1.5', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-sm transition-colors pointer-coarse:h-11',
              active ? 'bg-ink text-white' : 'border border-line bg-surface text-muted hover:text-ink',
            )}
          >
            {o.label}
            {o.count !== undefined ? (
              <span className={cn('rounded-sm px-1.5 text-xs font-medium tabular-nums', active ? 'bg-white/15 text-white' : 'bg-track text-muted')}>{fmt.number(o.count)}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ Pagination

export function Pagination({ total, limit, offset, onChange }: { total: number; limit: number; offset: number; onChange: (offset: number) => void }) {
  const t = useT();
  const fmt = useFormat();
  if (total <= limit && offset === 0) return null;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm text-subtle">
      <span>{t('common.showingOf', { from: fmt.number(from), to: fmt.number(to), total: fmt.number(total) })}</span>
      <div className="flex gap-2">
        <Button size="sm" icon={BackChevron} disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          {t('common.previous')}
        </Button>
        <Button size="sm" iconEnd={ForwardChevron} disabled={to >= total} onClick={() => onChange(offset + limit)}>
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}
